import axios from "axios";
import { BASE_URL } from "../Constant";

const VAT_RATE = 20; // Fallback VAT rate

/**
 * Get the retailer's pricing settings from cached localStorage.
 */
function getRetailerVatSettings() {
  try {
    const s = JSON.parse(localStorage.getItem("tdc_retailer_settings") || "{}");
    const g = s._globalPricing || {};
    return {
      isVatRegistered: s.Payment_Type === "vat" || s.Payment_Type === undefined,
      vatRate: g.vat_rate || VAT_RATE,
    };
  } catch {
    return { isVatRegistered: true, vatRate: VAT_RATE };
  }
}

/**
 * Get the full retailer pricing adjustments from cached localStorage.
 * Trade discount, cash markup and bad-payer surcharge all use the retailer
 * override if set, otherwise the global default.
 *
 * CASH MARKUP — settled 2026-09-10, do not un-settle it.
 * There used to be FOUR call sites and TWO rules: the product page honoured
 * `viewer.Cash_Markup || global`, while checkout and this helper used the
 * global alone. Every account has Cash_Markup = 0, so nothing was visibly
 * wrong — but the moment anyone set a per-retailer markup in Admin
 * (RetailerDetail.jsx exposes the field), the shelf price and the cart would
 * have quoted different numbers for the same item.
 *
 * All four now read the override first:
 *   Routes/BackendSide/Product/product_route.js  (shelf price, both handlers)
 *   utils/calculateCartPricing.js                (checkout)
 *   Routes/FrontendSide/Cart/cart_route.js       (checkout)
 *   here                                         (what the page renders)
 *
 * Change one and you must change all four, or the divergence comes straight
 * back. `Cash_Markup` is already on the /my-settings payload, so this needs
 * no new plumbing.
 */
function getRetailerPricing() {
  try {
    const s = JSON.parse(localStorage.getItem("tdc_retailer_settings") || "{}");
    const g = s._globalPricing || {};
    const isCash = s.Payment_Type === "cash";
    return {
      isCash,
      cashMarkup: isCash ? Number(s.Cash_Markup || g.default_cash_markup || 0) : 0,
      tradeDiscount: Number(s.Trade_Discount || g.default_trade_discount || 0),
      badPayerSurcharge: Number(s.Bad_Payer_Surcharge || g.default_bad_payer_surcharge || 0),
    };
  } catch {
    return { isCash: false, cashMarkup: 0, tradeDiscount: 0, badPayerSurcharge: 0 };
  }
}

/**
 * Round price up to nearest increment based on global setting.
 * "none" = no rounding, "0.5" = nearest £0.50, "1" = nearest £1, etc.
 */
function applyRounding(price) {
  try {
    const s = JSON.parse(localStorage.getItem("tdc_retailer_settings") || "{}");
    const rounding = s._globalPricing?.price_rounding || "none";
    if (rounding === "none") return price;
    const increment = Number(rounding);
    if (!increment || increment <= 0) return price;
    return Math.ceil(price / increment) * increment;
  } catch {
    return price;
  }
}

/**
 * Apply retailer-specific pricing adjustments to a base price.
 * Formula: price * (1 - tradeDiscount%) * (1 + cashMarkup%) * (1 + badPayerSurcharge%)
 * Then applies global rounding.
 */
function applyRetailerPricing(price) {
  const p = getRetailerPricing();
  let adjusted = Number(price || 0);
  if (p.tradeDiscount > 0) adjusted *= (1 - p.tradeDiscount / 100);
  if (p.cashMarkup > 0) adjusted *= (1 + p.cashMarkup / 100);
  if (p.badPayerSurcharge > 0) adjusted *= (1 + p.badPayerSurcharge / 100);
  return applyRounding(adjusted);
}

/**
 * Fetch retailer settings from API and update localStorage cache.
 */
async function refreshRetailerSettings() {
  const token = localStorage.getItem("token");
  if (!token) return;
  try {
    const res = await axios.get(`${BASE_URL}/user/my-settings`, { headers: { Authorization: token } });
    const settings = res?.data?.settings;
    if (settings) {
      localStorage.setItem("tdc_retailer_settings", JSON.stringify(settings));
    }
  } catch {
    // ignore — interceptor handles 401
  }
}

export { getRetailerVatSettings, getRetailerPricing, applyRetailerPricing, refreshRetailerSettings, VAT_RATE };
